import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SortingState, PaginationState } from '@tanstack/react-table';

interface UseTableStateParamsOptions {
  /** Prefix for URL params to avoid collisions when multiple tables share a page (e.g., "open" → "open_page") */
  prefix?: string;
  defaultPageSize?: number;
}

interface TableStateParams {
  sorting: SortingState;
  pagination: PaginationState;
  onSortingChange: (updater: SortingState | ((prev: SortingState) => SortingState)) => void;
  onPaginationChange: (updater: PaginationState | ((prev: PaginationState) => PaginationState)) => void;
  /** Reset page to 0 (useful when filtering changes) */
  resetPage: () => void;
}

function paramKey(prefix: string | undefined, key: string): string {
  return prefix ? `${prefix}_${key}` : key;
}

/**
 * Syncs TanStack Table sorting and pagination state with URL search params.
 * State survives page refresh and browser back/forward navigation.
 *
 * @example
 * const { sorting, pagination, onSortingChange, onPaginationChange } = useTableStateParams({ prefix: 'open' });
 * const table = useReactTable({ state: { sorting, pagination }, onSortingChange, onPaginationChange, ... });
 */
export function useTableStateParams(options: UseTableStateParamsOptions = {}): TableStateParams {
  const { prefix, defaultPageSize = 10 } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const pageKey = paramKey(prefix, 'page');
  const sizeKey = paramKey(prefix, 'size');
  const sortKey = paramKey(prefix, 'sort');
  const dirKey = paramKey(prefix, 'dir');

  const sorting: SortingState = useMemo(() => {
    const col = searchParams.get(sortKey);
    const dir = searchParams.get(dirKey);
    if (!col) return [];
    return [{ id: col, desc: dir === 'desc' }];
  }, [searchParams, sortKey, dirKey]);

  const pagination: PaginationState = useMemo(() => {
    const page = searchParams.get(pageKey);
    const size = searchParams.get(sizeKey);
    return {
      pageIndex: page ? Math.max(0, Number(page) - 1) : 0, // URL is 1-based, TanStack is 0-based
      pageSize: size ? Number(size) : defaultPageSize,
    };
  }, [searchParams, pageKey, sizeKey, defaultPageSize]);

  const onSortingChange = useCallback(
    (updater: SortingState | ((prev: SortingState) => SortingState)) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const currentSort = prev.get(sortKey);
        const currentDir = prev.get(dirKey);
        const currentState: SortingState = currentSort
          ? [{ id: currentSort, desc: currentDir === 'desc' }]
          : [];

        const newState = typeof updater === 'function' ? updater(currentState) : updater;

        if (newState.length > 0) {
          next.set(sortKey, newState[0].id);
          next.set(dirKey, newState[0].desc ? 'desc' : 'asc');
        } else {
          next.delete(sortKey);
          next.delete(dirKey);
        }
        // Reset to page 1 when sorting changes
        next.delete(pageKey);
        return next;
      }, { replace: true });
    },
    [setSearchParams, sortKey, dirKey, pageKey]
  );

  const onPaginationChange = useCallback(
    (updater: PaginationState | ((prev: PaginationState) => PaginationState)) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const currentPage = prev.get(pageKey);
        const currentSize = prev.get(sizeKey);
        const currentState: PaginationState = {
          pageIndex: currentPage ? Math.max(0, Number(currentPage) - 1) : 0,
          pageSize: currentSize ? Number(currentSize) : defaultPageSize,
        };

        const newState = typeof updater === 'function' ? updater(currentState) : updater;

        // Only write to URL if non-default
        if (newState.pageIndex > 0) {
          next.set(pageKey, String(newState.pageIndex + 1)); // 1-based in URL
        } else {
          next.delete(pageKey);
        }

        if (newState.pageSize !== defaultPageSize) {
          next.set(sizeKey, String(newState.pageSize));
        } else {
          next.delete(sizeKey);
        }

        return next;
      }, { replace: true });
    },
    [setSearchParams, pageKey, sizeKey, defaultPageSize]
  );

  const resetPage = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(pageKey);
      return next;
    }, { replace: true });
  }, [setSearchParams, pageKey]);

  return { sorting, pagination, onSortingChange, onPaginationChange, resetPage };
}
